/* eslint-disable no-unused-vars */
/* eslint-disable eqeqeq */
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, fileUpload, get } from "../../config";
import { useBankList } from "../../lib/OptionsAPI";
import { formatBSBNumber, MaxLimit, padWithLeadingZeros } from "../../lib/Misc";
import { AddWithdrawalAccountSchema } from "../../lib/Validation";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import CustomLabelText from "../../components/CustomLabelText";
import { Label } from "../../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import { Loader2, LucideInfo } from "lucide-react";
import { Input } from "../../components/ui/input";
import { CardTitle } from "../../components/ui/card";
import SearchSelect from "../../components/SearchSelect";
import { NumericFormat } from "react-number-format";
import ReactInputMask from "react-input-mask";
import Spin from "../../components/Spin";
import { useTrust } from "../../lib/context/TrustContext";
import { useOrganization } from "../../lib/context/OrganisationContext";

const WithdrawalAccount = ({ edit, toggleShowAddEditTrust, tab = false }) => {
  const { trust_id } = useParams();
  const [withdrawalAccount, setWithdrawalAccount] = useState();
  const [loading, setLoading] = useState(false);
  const { organizationData } = useOrganization();
  const { fetchTrustList, updateTrustData } = useTrust();

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.WITHDRAWAL_ACCOUNT}/${trust_id}`);
      setWithdrawalAccount(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setWithdrawalAccount(null);
    }
  }
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trust_id]);

  // Submit Form
  const handleFormSubmit = async (value) => {
    const formData = new FormData();
    formData.append(
      "account_name",
      value?.account_name ? value?.account_name : ""
    );
    formData.append("bsb", value?.bsb ? formatBSBNumber(value?.bsb) : "");
    formData.append(
      "account_number",
      value?.account_number ? value?.account_number : ""
    );
    formData.append(
      "opening_balance",
      value?.opening_balance ? value?.opening_balance : 0
    );
    formData.append("trust_id", trust_id ? trust_id : "");

    formData.append("apca_id", value?.apca_id ? value?.apca_id : "");

    formData.append("bank_id", value?.bank_id ? value?.bank_id : "");

    formData.append("type", 1);

    try {
      setLoading(true);
      const { data } = await fileUpload(`${API.WITHDRAWAL_ACCOUNT}`, formData);
      setLoading(false);

      updateTrustData(data?.data);
      if (
        organizationData?.organizationId &&
        organizationData?.organizationId !== undefined
      ) {
        fetchTrustList(organizationData?.organizationId);
      }
      return data?.data;
    } catch (error) {
      setWithdrawalAccount(value);
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const { bankLoading, bankList = [] } = useBankList();
  const bankListing = bankList ? bankList : [];
  const projectBank = bankListing?.find(
    (item) => item?.value === withdrawalAccount?.bank_id
  )?.value;

  return (
    <Formik
      initialValues={{
        account_name: withdrawalAccount?.account_name
          ? withdrawalAccount?.account_name
          : "",
        bsb: withdrawalAccount?.bsb ? withdrawalAccount?.bsb : "",
        account_number: withdrawalAccount?.account_number
          ? withdrawalAccount?.account_number
          : "",
        opening_balance: withdrawalAccount?.opening_balance
          ? withdrawalAccount?.opening_balance
          : 0,
        bank_id: projectBank ? projectBank : "",
        apca_id: withdrawalAccount?.apca_id
          ? padWithLeadingZeros(withdrawalAccount?.apca_id, 6)
          : "",
      }}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddWithdrawalAccountSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div
              className={`form-box overflow-hidden overflow-y-auto ${
                tab ? "max-h-[69vh]" : "max-h-[80vh]"
              } p-4 border rounded-lg`}
            >
              <div className="grid gap-4">
                <CardTitle className="text-md">
                  General / Withdrawal Bank Account Details
                </CardTitle>
                <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <div className="min-w-0">
                        <Label htmlFor="bank_id">
                          <CustomLabelText
                            required={true}
                            labelText="Bank Name"
                            errorField={errors?.bank_id}
                          />
                        </Label>
                        <div className="relative mt-2">
                          <SearchSelect
                            frameworks={bankListing}
                            optionType={"Bank"}
                            handleSelect={(e) => {
                              setFieldValue("bank_id", e);
                            }}
                            handleBlur={handleBlur}
                            className={
                              errors?.bank_id &&
                              `focus-visible:ring-red-500 border-red-500`
                            }
                            disabled={values?.trust_id ? true : false}
                            initialValue={values?.bank_id}
                          />
                          {bankLoading && (
                            <span className="absolute top-2 right-2 animate-spin">
                              <Loader2 width={20} height={20} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="account_name">
                        <CustomLabelText
                          required={true}
                          labelText="Bank Account Name"
                          errorField={errors?.account_name}
                        />
                      </Label>
                      <Input
                        type="text"
                        name="account_name"
                        value={values?.account_name ? values?.account_name : ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                        onKeyPress={(e) => {
                          e.which === 13 && e.preventDefault();
                        }}
                        disabled={values?.trust_id ? true : false}
                        className={
                          errors?.account_name &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="bsb">
                        <CustomLabelText
                          required={true}
                          labelText="BSB"
                          errorField={errors?.bsb}
                        />
                      </Label>
                      <ReactInputMask
                        id="bsb"
                        type="text"
                        name="bsb"
                        value={values?.bsb ? values?.bsb : ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        mask="999-999"
                        maskChar={null}
                        autoComplete="off"
                        onKeyPress={(e) => {
                          e.which === 13 && e.preventDefault();
                        }}
                        disabled={values?.trust_id ? true : false}
                        className={
                          errors?.bsb
                            ? `${cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )} focus-visible:ring-red-500 border-red-500`
                            : cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="account_number">
                        <CustomLabelText
                          required={true}
                          labelText="Account Number"
                          errorField={errors?.account_number}
                        />
                      </Label>
                      <ReactInputMask
                        id="account_number"
                        type="text"
                        name="account_number"
                        value={
                          values?.account_number ? values?.account_number : ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        mask="999999999"
                        maskChar={null}
                        autoComplete="off"
                        onKeyPress={(e) => {
                          e.which === 13 && e.preventDefault();
                        }}
                        disabled={values?.trust_id ? true : false}
                        className={
                          errors?.account_number
                            ? `${cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )} focus-visible:ring-red-500 border-red-500`
                            : cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )
                        }
                      />
                    </div>
                  </div>
                  {/* <div className="grid md:grid-cols-2 gap-4 items-baseline">
                    <div className="grid gap-2">
                      <Label
                        htmlFor="apca_id"
                        className="flex items-center gap-2"
                      >
                        {!values?.trust_id && (
                          <div className="apca-info">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  className="rounded-full h-4 w-4"
                                >
                                  <LucideInfo className="h-4 w-4" />
                                  <span className="sr-only">APCA ID Info</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFieldValue(`apca_id`, "000000");
                                  }}
                                >
                                  ANZ - 000000
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFieldValue(`apca_id`, "000000");
                                  }}
                                >
                                  Westpac - 000000
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFieldValue(`apca_id`, "000000");
                                  }}
                                >
                                  NAB - 000000
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFieldValue(`apca_id`, "301500");
                                  }}
                                >
                                  CBA - 301500
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setFieldValue(`apca_id`, "651966");
                                  }}
                                >
                                  BBL - 651966
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                        <CustomLabelText
                          required={false}
                          labelText="APCA ID"
                          errorField={errors?.apca_id}
                        />
                      </Label>
                      <ReactInputMask
                        mask="999999"
                        id="apca_id"
                        name="apca_id"
                        value={
                          values?.apca_id?.toString() ? values?.apca_id : ""
                        }
                        placeholder="APCA ID"
                        maskChar=""
                        onChange={(e) => {
                          setFieldValue(
                            `apca_id`,
                            e?.target?.value ? e?.target?.value : ""
                          );
                        }}
                        onBlur={(e) => {
                          setFieldValue(
                            `apca_id`,
                            e?.target?.value
                              ? padWithLeadingZeros(e?.target?.value, 6)
                              : ""
                          );
                        }}
                        disabled={values?.trust_id ? true : false}
                        className={
                          errors?.apca_id
                            ? `${cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )} focus-visible:ring-red-500 border-red-500`
                            : cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="opening_balance">
                        <CustomLabelText
                          required={false}
                          labelText="Opening Balance($)"
                          errorField={errors?.opening_balance}
                        />
                      </Label>

                      <NumericFormat
                        type="text"
                        name="opening_balance"
                        value={values?.opening_balance}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={true}
                        isAllowed={(values) => MaxLimit(values)}
                        autoComplete="off"
                        onKeyPress={(e) => {
                          e.which === 13 && e.preventDefault();
                        }}
                        disabled={values?.trust_id ? true : false}
                        className={
                          errors?.opening_balance
                            ? `${cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )} focus-visible:ring-red-500 border-red-500`
                            : cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              )
                        }
                      />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default WithdrawalAccount;
